import { Badge, Button, Modal, Spinner } from "react-bootstrap";

import {
  formatRegistrationDate,
  getCategoryTitle,
  getDriverEmail,
  getDriverImageUrl,
  getDriverName,
  getInheritedConsentInfo,
  getNavigatorName,
  getStatusBadgeVariant,
  getTeamName,
  getTeamNumber,
  getVehicleSummary,
} from "../utils/competitors.utils";

const DetailRow = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-muted small mb-1">{label}</div>
    <div>{value || "-"}</div>
  </div>
);

const RegistrationDetailModal = ({
  show,
  registration,
  categories = [],
  isLoading = false,
  canManage = false,
  approving = false,
  uploading = false,
  onClose,
  onApprove,
  onUnapprove,
  onDownloadConsent,
  onDownloadSignedConsent,
  onUploadSignedConsent,
}) => {
  const status = registration?.status || "";
  const consent = registration
    ? getInheritedConsentInfo(registration, categories)
    : null;
  const imageUrl = registration ? getDriverImageUrl(registration) : "";
  const id = registration?._id || registration?.id;

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Registration detail</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && !registration ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : !registration ? (
          <p className="text-muted mb-0">Registration not found.</p>
        ) : (
          <>
            <div className="d-flex gap-3 align-items-start mb-4">
              <div
                className="d-flex align-items-center justify-content-center overflow-hidden border rounded"
                style={{ width: 72, height: 72, flexShrink: 0 }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={getDriverName(registration)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span className="text-muted small">No photo</span>
                )}
              </div>
              <div>
                <h5 className="mb-1">{getDriverName(registration)}</h5>
                <div className="text-muted small mb-2">
                  {getDriverEmail(registration)}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Badge bg={getStatusBadgeVariant(status)}>
                    {status || "-"}
                  </Badge>
                  <Badge bg={consent?.isSigned ? "success" : "secondary"}>
                    {consent?.isSigned ? "Consent signed" : "Consent not signed"}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <DetailRow label="Team" value={getTeamName(registration)} />
                <DetailRow
                  label="Team number"
                  value={getTeamNumber(registration)}
                />
                <DetailRow
                  label="Category"
                  value={getCategoryTitle(registration)}
                />
              </div>
              <div className="col-md-6">
                <DetailRow
                  label="Navigator"
                  value={getNavigatorName(registration)}
                />
                <DetailRow
                  label="Vehicle"
                  value={getVehicleSummary(registration)}
                />
                <DetailRow
                  label="Registered at"
                  value={formatRegistrationDate(
                    registration.registered_at || registration.createdAt
                  )}
                />
              </div>
            </div>

            {id ? (
              <DetailRow label="Registration ID" value={<code>{id}</code>} />
            ) : null}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {consent?.hasConsentTemplate ? (
          <Button
            variant="outline-secondary"
            onClick={() => onDownloadConsent(registration)}
          >
            Download consent
          </Button>
        ) : null}
        {consent?.signedUrl ? (
          <Button
            variant="outline-secondary"
            onClick={() => onDownloadSignedConsent(consent.signedUrl)}
          >
            Download signed consent
          </Button>
        ) : null}
        {canManage && consent?.categoryId ? (
          <Button
            variant="outline-primary"
            disabled={uploading}
            onClick={() => onUploadSignedConsent(registration)}
          >
            {uploading
              ? "Uploading..."
              : consent.isSigned
                ? "Replace signed consent"
                : "Upload signed consent"}
          </Button>
        ) : null}
        {canManage && status === "pending" ? (
          <Button
            variant="success"
            disabled={approving || !id}
            onClick={() => onApprove(registration)}
          >
            {approving ? "Approving..." : "Approve"}
          </Button>
        ) : null}
        {canManage && status === "approved" ? (
          <Button
            variant="warning"
            disabled={approving || !id}
            onClick={() => onUnapprove(registration)}
          >
            {approving ? "Updating..." : "Set pending"}
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RegistrationDetailModal;
